import { useState } from 'react';
import { useInvalidateUserTokens } from '../../../hooks/useInvalidateUserTokens';
import {
  ConfirmDialog,
  customAlertApiRef,
  WbButton,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@mainstage/core-plugin-api';
import { Typography } from '@material-ui/core';
import { usePermission } from '@mainstage/plugin-permission-react';
import { platformUsersAuthInvalidateTokensPermission } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef, UserEntity } from '@mainstage/catalog-model';

export function InvalidateUserTokensButton({ user }: { user: UserEntity }) {
  const [open, setOpen] = useState(false);
  const { mutate: invalidateUserTokens, isLoading } = useInvalidateUserTokens();
  const alertApi = useApi(customAlertApiRef);
  const { allowed: canInvalidateTokens } = usePermission({
    permission: platformUsersAuthInvalidateTokensPermission,
  });
  const handleOnClose = () => setOpen(false);

  // Hide the button if the user doesn't have the required permission
  if (!canInvalidateTokens) return null;

  const {
    metadata: { name: metaName },
    spec: { profile },
  } = user;
  const displayName = profile?.displayName ?? metaName;

  return (
    <>
      <WbTooltip title={`Invalidate all issued JWT tokens for ${displayName}`}>
        <WbButton
          label="Invalidate user tokens"
          variant="outlined"
          onClick={() => {
            setOpen(true);
          }}
        />
      </WbTooltip>
      <ConfirmDialog
        title={`Are you sure you want to invalidate all active tokens for ${displayName}?`}
        inProgress={isLoading}
        description={
          <>
            <Typography style={{ fontWeight: '700' }}>
              All existing JWT tokens for this user will be invalidated and can
              no longer be used.
            </Typography>
            <br />
            <Typography>
              The user will need to re-authenticate or refresh their session
              through the authentication provider to regain access.
            </Typography>
            <br />
            <Typography>
              This is not a user ban or a forced logout mechanism. It should
              only be used if you suspect that the user's tokens have been
              compromised or tampered with.
            </Typography>
            <br />
            <Typography style={{ fontWeight: '700' }}>
              This action cannot be undone.
            </Typography>
          </>
        }
        confirmButtonText="Invalidate user tokens"
        onConfirm={() =>
          invalidateUserTokens(
            { userRef: stringifyEntityRef(user) },
            {
              onSuccess: () => {
                handleOnClose();
                alertApi.post({
                  severity: 'success',
                  message: 'Tokens invalidated successfully',
                });
              },
              onError: e => {
                handleOnClose();
                alertApi.post({
                  severity: 'error',
                  message: (e instanceof Error
                    ? e
                    : new Error('Something went wrong')
                  ).message,
                });
              },
            },
          )
        }
        open={open}
        onClose={handleOnClose}
      />
    </>
  );
}
